import useBackendClient from "@/client";
import useToast from "@/hooks/useToastNotification";
import { useDeleteStoreItemSpriteMutation, useGetStoreItemSpritesQuery } from "@/graphql";
import { computed } from "vue";

const { toastError } = useToast();

const useUploadSprites = (gameId: string, versionId: string) => {
  const { client, useAxios } = useBackendClient();
  const { isLoading, execute: uploadSpriteCall } = useAxios(
    `/app/${gameId}/version/${versionId}/sprites/upload`,
    {
      method: "post",
    },
    client,
    { immediate: false }
  );
  return {
    isLoading,
    uploadSpriteCall,
  };
};

export default function useSprites(gameId: string, versionId: string, spriteType: string) {
  const {
    result,
    loading: getSpritesLoading,
    refetch: reFetchSprites,
  } = useGetStoreItemSpritesQuery({ gameId, versionId, type: spriteType }, { fetchPolicy: "cache-and-network" });
  const { mutate: deleteStoreItemSprite } = useDeleteStoreItemSpriteMutation({});
  const { uploadSpriteCall, isLoading: uploadSpriteLoading } = useUploadSprites(gameId, versionId);
  const isWIP = computed(() => versionId === "wip");
  const sprites = computed(() => result.value?.getStoreItemSprites || []);
  const uploadSprite = async (files: File[], type: string) => {
    try {
      if (!isWIP.value) {
        toastError({
          detail: "You cannot upload sprites on non WIP version.",
          summary: "Error",
        });
        return;
      }
      if (files.length >= 10) {
        toastError({
          detail: "You can only upload 10 sprites at a time.",
          summary: "Error",
        });
        return;
      }
      const formData = new FormData();
      formData.append("type", type);
      files.forEach((file) => {
        formData.append("files", file);
      });
      await uploadSpriteCall({
        data: formData,
      });

      await reFetchSprites();
    } catch (error: { response: { status: number } }) {
      if (error?.response?.status === 409) {
        toastError({
          detail: "Duplicate sprite uploaded, ensure that names are unique.",
          summary: "Error",
        });
        return;
      }
      toastError({
        detail: "Failed to upload sprite. Please try again.",
        summary: "Error",
      });
    }
  };

  const deleteSprite = async (spriteId: string) => {
    if (!isWIP.value) {
      toastError({
        detail: "You cannot delete sprites on non WIP version.",
        summary: "Error",
      });
      return;
    }
    await deleteStoreItemSprite(
      {
        gameId,
        spriteId,
        versionId,
      },
      {
        update: (cache) => {
          cache.evict({ id: `StoreItemSprite:${spriteId}` });
        },
        optimisticResponse: {
          __typename: "Mutation",
          deleteStoreItemSprite: true,
        },
      }
    );
  };

  return {
    sprites,
    deleteSprite,
    getSpritesLoading,
    uploadSpriteLoading,
    reFetchSprites,
    uploadSprite,
  };
}
